import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import AdTikTokPreview from './AdTikTokPreview'
import SkeletonTikTokAdPreview from './AdTikTokPreview/Skeleton'
import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from './Sections/NameSection/NameSectionPreview'
import NameSectionForm from './Sections/NameSection/NameSectionForm'
import TextSectionPreview from './Sections/TextSection/TextSectionPreview'
import TextSectionForm from './Sections/TextSection/TextSectionForm'
import ClickThroughLinkSectionPreview from './Sections/ClickThroughLinkSection/ClickThroughLinkSectionPreview'
import ClickThroughLinkSectionForm from './Sections/ClickThroughLinkSection/ClickThroughLinkSectionForm'
import CTASectionPreview from './Sections/CTASection/CTASectionPreview'
import CTASectionForm from './Sections/CTASection/CTASectionForm'
import VideoSectionPreview from './Sections/VideoSection/VideoSectionPreview'
import VideoSectionForm from './Sections/VideoSection/VideoSectionForm'

import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'

import { createJsonFromQueryString } from '../../../../helpers/url'

import { getAd, clearAd } from '../../../../modules/actions/ads'
import { getTikTokIdentities } from '../../../../modules/actions/tikTokConstants'
import { tikTokIdentitiesWasLoadedSelector } from '../../../../modules/selectors/tikTokConstants'
import { selectedTikTokAdAccountSelector } from '../../../../modules/selectors/adAccounts'
import { adSelector, adsSelector, adWasLoadedSelector } from '../../../../modules/selectors/ads'

import { AD_TIK_TOK_EDIT } from '../../../../constants/forms'
import { NAME } from './fields'
import { TIKTOK_PLATFORM } from '../../../../constants/selectLists/platformList'
import { VIDEO, AD_TEXT, CALL_TO_ACTION, LANDING_PAGE_URL } from '../fields'

// here defined initial open states for function AdTikTokEdit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [AD_TEXT]: false,
  [LANDING_PAGE_URL]: false,
  [CALL_TO_ACTION]: false,
  [VIDEO]: false
}

const AdTikTokEdit = () => {
  const dispatch = useDispatch()

  const ads = useSelector(adsSelector)
  const ad = useSelector(adSelector)

  const adWasLoaded = useSelector(adWasLoadedSelector)
  const tikTokIdentitiesWasLoaded = useSelector(tikTokIdentitiesWasLoadedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const isInitialDataLoading = useMemo(() => {
    return !adWasLoaded
  }, [adWasLoaded])

  // if ad is internal, it appears in query params when we open edit form
  const { internal: internalQueryParam } = createJsonFromQueryString(window.location.search)

  const isInternalAd = internalQueryParam === 'true'

  const getAdDataHandler = useCallback(
    selectedEditItemId => {
      if (adWasLoaded) {
        // if ad data was already loaded, we don't need to load it again
        return
      }

      const currentAd = ads.find(({ id }) => Number(id) === Number(selectedEditItemId))
      const currentAdAdAccountId = currentAd?.ad_account_id

      const adAccount = currentAdAdAccountId || selectedAdAccountId

      dispatch(
        getAd(
          {
            id: selectedEditItemId,
            account: adAccount,
            ...(isInternalAd && {
              internal: true
            })
          },
          TIKTOK_PLATFORM
        )
      )
    },
    [dispatch, isInternalAd, selectedAdAccountId, ads, adWasLoaded]
  )

  const clearAdDataHandler = useCallback(() => {
    dispatch(clearAd())
  }, [dispatch])

  useManageEditFormData({
    formName: AD_TIK_TOK_EDIT,
    loadedDataId: ad.id,
    getDataHandler: getAdDataHandler,
    clearDataHandler: clearAdDataHandler
  })

  // here defined all edit sections for AdTikTokEdit
  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview />,
        FormComponent: <NameSectionForm />
      },
      {
        sectionName: AD_TEXT,
        title: 'Text',
        PreviewComponent: <TextSectionPreview />,
        FormComponent: <TextSectionForm />
      },
      {
        sectionName: LANDING_PAGE_URL,
        title: 'Click Through Link',
        PreviewComponent: <ClickThroughLinkSectionPreview />,
        FormComponent: <ClickThroughLinkSectionForm />
      },
      {
        sectionName: CALL_TO_ACTION,
        title: 'Call To Action',
        PreviewComponent: <CTASectionPreview />,
        FormComponent: <CTASectionForm />
      },
      {
        sectionName: VIDEO,
        title: 'Video',
        PreviewComponent: <VideoSectionPreview />,
        FormComponent: <VideoSectionForm />
      }
    ],
    []
  )

  useEffect(() => {
    if (!tikTokIdentitiesWasLoaded) {
      dispatch(getTikTokIdentities({ account: selectedAdAccountId }))
    }
  }, [dispatch, selectedAdAccountId, tikTokIdentitiesWasLoaded])

  return (
    <FormDrawerWrapper title="Edit a TikTok ad" formName={AD_TIK_TOK_EDIT} showOpenButton={false}>
      {isInitialDataLoading ? <SkeletonTikTokAdPreview /> : <AdTikTokPreview adData={ad} isEditMode />}
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={ad}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
      />
    </FormDrawerWrapper>
  )
}

export default AdTikTokEdit
